import React, { useState, useEffect, useCallback } from "react";
import { useAuth, API } from "../App";
import { useNavigate, Routes, Route, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import Logbook from "./Logbook";
import ReactMarkdown from "react-markdown";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { 
  Waves, 
  Calendar as CalendarIcon, 
  Clock, 
  Users, 
  Star,
  LogOut,
  User,
  Check,
  ChevronRight,
  X,
  MapPin,
  CreditCard,
  ShoppingBag,
  Sparkles,
  Award,
  Globe,
  MessageSquare,
  Home,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  Bell,
  ExternalLink,
  GraduationCap,
  QrCode,
  CheckCircle,
} from "lucide-react";
import { format, parseISO, isAfter, isBefore, isToday, addHours, subHours } from "date-fns";
import { pt, enUS } from "date-fns/locale";
import { useTranslation } from "../lib/i18n";

const StudentDashboard = () => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, lang } = useTranslation();
  const dateFnsLocale = lang === "en" ? enUS : pt;
  const [lessons, setLessons] = useState([]);
  const [myEnrollments, setMyEnrollments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [enrollingLessonId, setEnrollingLessonId] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [publicSettings, setPublicSettings] = useState({ payments_active: true, whatsapp_number: "" });
  const [collapsed, setCollapsed] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activityDialog, setActivityDialog] = useState({ open: false, lessonId: null });
  const [mySubscription, setMySubscription] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);

  const menuItems = [
    { path: "/dashboard", icon: Home, label: t("home"), exact: true },
    { path: "/dashboard/lessons", icon: Waves, label: t("lessons") },
    { path: "/dashboard/progress", icon: Star, label: t("progress") },
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [lessonsRes, enrollmentsRes, settingsRes, subRes, notifsRes] = await Promise.all([
        axios.get(`${API}/lessons?status=active`, { withCredentials: true }),
        axios.get(`${API}/enrollments/my`, { withCredentials: true }),
        fetch(`${API}/settings/public`).then(r => r.ok ? r.json() : { payments_active: true, whatsapp_number: "" }),
        axios.get(`${API}/subscriptions/my`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${API}/notifications`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setLessons(lessonsRes.data);
      setMyEnrollments(enrollmentsRes.data);
      setPublicSettings(settingsRes);
      setMySubscription(subRes.data);
      setNotifications(notifsRes.data || []);
      setUnreadCount((notifsRes.data || []).filter(n => !n.read).length);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error(t("errorLoad"));
    } finally {
      setLoading(false);
    }
  };

  const handleEnroll = async (lessonId, activityType = "surf") => {
    try {
      setEnrollingLessonId(lessonId);
      const res = await axios.post(
        `${API}/enrollments`,
        { lesson_id: lessonId, activity_type: activityType },
        { withCredentials: true }
      );
      if (res.data.status === "reserved") {
        toast.info(t("reservedNoCredits"), {
          description: t("reservedDescription"),
          action: { label: t("buy"), onClick: () => navigate("/packs") },
        });
      } else {
        toast.success(t("enrollSuccess"));
      }
      fetchData();
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error(t("noCreditsReservation"), {
          description: error.response?.data?.detail,
          action: { label: t("buy"), onClick: () => navigate("/packs") },
        });
      } else {
        toast.error(error.response?.data?.detail || t("errorEnroll"));
      }
    } finally {
      setEnrollingLessonId(null);
    }
  };

  const startEnroll = (lessonId) => {
    // Find the lesson to check its category
    const lesson = lessons.find(l => l.lesson_id === lessonId);
    if (lesson) {
      // Skip activity dialog for single-category lessons
      if (lesson.lesson_category === "surf") {
        handleEnroll(lessonId, "surf");
        return;
      } else if (lesson.lesson_category === "bodyboard") {
        handleEnroll(lessonId, "bodyboard");
        return;
      }
    }
    setActivityDialog({ open: true, lessonId });
  };

  const confirmEnroll = (activityType) => {
    const lessonId = activityDialog.lessonId;
    setActivityDialog({ open: false, lessonId: null });
    handleEnroll(lessonId, activityType);
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/mark-read`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleSubscribe = async (useStripe = true) => {
    try {
      if (useStripe && settings?.stripe_enabled) {
        // Use Stripe Checkout for automatic recurring billing
        const res = await axios.post(`${API}/subscriptions/checkout`, {
          origin_url: window.location.origin
        }, { withCredentials: true });
        
        if (res.data.url) {
          window.location.href = res.data.url;
          return;
        }
      }
      
      // Fallback to manual subscription request
      await axios.post(`${API}/subscriptions`, {}, { withCredentials: true });
      toast.success(lang === "en" ? "Subscription requested! Waiting for activation." : "Subscrição solicitada! Aguarda ativação.");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const handleCancelSubscription = async () => {
    if (!mySubscription) return;
    try {
      // If it's a Stripe subscription, cancel via Stripe endpoint
      if (mySubscription.stripe_subscription_id) {
        await axios.post(`${API}/subscriptions/cancel-stripe`, {}, { withCredentials: true });
        toast.success(lang === "en" ? "Subscription will be cancelled at end of billing period" : "Subscrição será cancelada no fim do período de faturação");
      } else {
        await axios.delete(`${API}/subscriptions/${mySubscription.subscription_id}`, { withCredentials: true });
        toast.success(lang === "en" ? "Subscription cancelled" : "Subscrição cancelada");
      }
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const handleReactivateSubscription = async () => {
    if (!mySubscription) return;
    try {
      await axios.post(`${API}/subscriptions/${mySubscription.subscription_id}/reactivate`, {}, { withCredentials: true });
      toast.success(lang === "en" ? "Subscription reactivated" : "Subscrição reativada");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const handleCancelEnrollment = async (enrollmentId) => {
    try {
      await axios.delete(`${API}/enrollments/${enrollmentId}`, { withCredentials: true });
      toast.success(t("cancelSuccess"));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t("errorCancel"));
    }
  };

  const canCancel = (enrollment) => {
    if (!enrollment?.lesson?.date) return false;
    const now = new Date();
    const lessonDate = new Date(enrollment.lesson.date + "T00:00:00");
    const deadline = new Date(lessonDate);
    deadline.setDate(deadline.getDate() - 1);
    deadline.setHours(20, 0, 0, 0);
    return now < deadline;
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleToggleLanguage = async () => {
    const newLang = user?.language === "en" ? "pt" : "en";
    try {
      await axios.patch(`${API}/auth/language`, { language: newLang }, { withCredentials: true });
      if (refreshUser) await refreshUser();
      toast.success(newLang === "pt" ? t("langChangedPt") : t("langChangedEn"));
    } catch {
      toast.error(t("errorLanguage"));
    }
  };

  const getEnrollmentStatus = (lessonId) => {
    return myEnrollments.find(
      (e) => e.lesson_id === lessonId && ["enrolled", "confirmed", "reserved"].includes(e.status)
    );
  };

  const filteredLessons = lessons.filter((lesson) => {
    if (lesson.lesson_type === "private") return false;
    const lessonDate = parseISO(lesson.date);
    return format(lessonDate, "yyyy-MM-dd") === format(selectedDate, "yyyy-MM-dd");
  });

  const upcomingEnrollments = myEnrollments.filter(
    (e) => e.lesson && ["enrolled", "confirmed", "reserved"].includes(e.status) && 
           (isAfter(parseISO(e.lesson.date), new Date()) || isToday(parseISO(e.lesson.date)))
  );

  const upcomingAvailable = lessons.filter(l => {
    const lessonDate = parseISO(l.date);
    if (!isAfter(lessonDate, new Date()) && !isToday(lessonDate)) return false;
    if (l.lesson_type === "private") return false;
    if (l.current_enrollments >= l.max_students) return false;
    const enrolled = getEnrollmentStatus(l.lesson_id);
    if (enrolled) return false;
    return true;
  }).slice(0, 6);

  const lessonDates = lessons.map((l) => parseISO(l.date));

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f5]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#6697a4] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#6697a4] font-medium">{t("loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f5f5] flex">
      {/* Mobile Overlay */}
      {mobileOpen && <div className="fixed inset-0 bg-black/30 z-40 md:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full bg-white border-r border-slate-100 z-50 transition-all duration-300 flex flex-col
        ${mobileOpen ? 'translate-x-0 w-56' : '-translate-x-full w-56'} md:translate-x-0 ${collapsed ? 'md:w-[68px]' : 'md:w-56'}
      `}>
        <div className="p-4 border-b border-slate-100 flex items-center justify-between">
          {(!collapsed || mobileOpen) && (
            <Link to="/" className="flex items-center gap-2">
              <img src="/assets/images/logo.png" alt="Apúlia Surf School" className="h-8 w-auto" />
            </Link>
          )}
          <button onClick={() => { setCollapsed(!collapsed); setMobileOpen(false); }} className="hidden md:block p-1.5 rounded-lg hover:bg-slate-100">
            {collapsed ? <PanelLeftOpen className="h-4 w-4 text-[#94A3B8]" /> : <PanelLeftClose className="h-4 w-4 text-[#94A3B8]" />}
          </button>
          <button onClick={() => setMobileOpen(false)} className="md:hidden p-1.5"><X className="h-5 w-5" /></button>
        </div>

        <ScrollArea className="flex-1 py-2">
          <nav className="px-2 space-y-1">
            {menuItems.map((item) => {
              const isActive = item.exact ? location.pathname === item.path : location.pathname.startsWith(item.path) && item.path !== "/dashboard";
              const isHomeActive = item.exact && location.pathname === item.path;
              const active = item.exact ? isHomeActive : isActive;
              return (
                <Link key={item.path} to={item.path} onClick={() => setMobileOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    active ? 'bg-[#6697a4] text-white shadow-sm' : 'text-[#475569] hover:bg-slate-50'
                  }`} title={collapsed ? item.label : undefined} data-testid={`nav-${item.path.split('/').pop() || 'home'}`}
                >
                  <item.icon className="h-5 w-5 shrink-0" />
                  {(!collapsed || mobileOpen) && <span>{item.label}</span>}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        {(!collapsed || mobileOpen) && (
          <div className="p-3 border-t border-slate-100">
            <div className="flex items-center gap-3 p-2">
              <Avatar className="h-8 w-8"><AvatarImage src={user?.picture} /><AvatarFallback className="bg-[#6697a4] text-white text-xs">{user?.name?.charAt(0)}</AvatarFallback></Avatar>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium text-[#3d5a63] truncate">{user?.name}</p></div>
            </div>
          </div>
        )}
      </aside>

      {/* Main Content */}
      <div className={`flex-1 transition-all duration-300 ${collapsed ? 'md:ml-[68px]' : 'md:ml-56'}`}>
        {/* Top Bar */}
        <header className="bg-white border-b border-slate-100 sticky top-0 z-30 px-4 md:px-6 py-3 flex items-center justify-between">
          <button onClick={() => setMobileOpen(true)} className="md:hidden p-2 hover:bg-slate-50 rounded-lg"><Menu className="h-5 w-5" /></button>
          <div className="hidden md:block">
            <h1 className="text-lg font-bold text-[#3d5a63]">{t("hello")}, {user?.name?.split(' ')[0]}!</h1>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="text-sm text-[#6697a4] hover:text-[#3d5a63] hidden md:flex items-center gap-1" data-testid="home-link">
              <ExternalLink className="h-3.5 w-3.5" /> Site
            </a>
            {/* Notifications Bell */}
            <div className="relative">
              <Button variant="ghost" size="sm" className="relative p-2" onClick={() => { setShowNotifications(!showNotifications); if (!showNotifications && unreadCount > 0) handleMarkAllRead(); }} data-testid="notifications-bell">
                <Bell className="h-5 w-5 text-[#475569]" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#e8927c] text-white text-[10px] rounded-full flex items-center justify-center">{unreadCount}</span>
                )}
              </Button>
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-100 z-50 overflow-hidden" data-testid="notifications-dropdown">
                  <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                    <p className="font-semibold text-sm text-[#3d5a63]">{lang === "en" ? "Notifications" : "Notificações"}</p>
                    <Button variant="ghost" size="sm" className="text-xs" onClick={() => setShowNotifications(false)}><X className="h-3 w-3" /></Button>
                  </div>
                  <ScrollArea className="max-h-72">
                    {notifications.length === 0 ? (
                      <p className="p-4 text-sm text-[#94A3B8] text-center">{lang === "en" ? "No notifications" : "Sem notificações"}</p>
                    ) : (
                      notifications.slice(0, 15).map(n => (
                        <div key={n.notification_id} className={`p-3 border-b border-slate-50 hover:bg-slate-50 cursor-pointer ${!n.read ? "bg-blue-50/50" : ""}`} onClick={() => { if (n.link) navigate(n.link); setShowNotifications(false); }}>
                          <p className="text-sm font-medium text-[#3d5a63]">{n.title}</p>
                          <p className="text-xs text-[#94A3B8] mt-0.5">{n.message}</p>
                        </div>
                      ))
                    )}
                  </ScrollArea>
                </div>
              )}
            </div>
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2 hover:bg-slate-50" data-testid="user-menu-btn">
                <Avatar className="h-8 w-8"><AvatarImage src={user?.picture} /><AvatarFallback className="bg-[#6697a4] text-white text-xs">{user?.name?.charAt(0)}</AvatarFallback></Avatar>
                <span className="hidden md:block text-sm font-medium text-[#3d5a63]">{user?.name}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={() => navigate("/profile/settings")} className="cursor-pointer" data-testid="profile-settings-btn"><User className="mr-2 h-4 w-4" />{t("profile")}</DropdownMenuItem>
              <DropdownMenuItem onClick={handleToggleLanguage} className="cursor-pointer" data-testid="language-toggle-btn"><Globe className="mr-2 h-4 w-4" />{user?.language === "en" ? t("switchToPt") : t("switchToEn")}</DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600" data-testid="logout-btn"><LogOut className="mr-2 h-4 w-4" />{t("logout")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        <main className="p-4 md:p-6">
          <Routes>
            <Route path="/" element={
              <StudentHome 
                user={user} upcomingEnrollments={upcomingEnrollments} upcomingAvailable={upcomingAvailable} 
                publicSettings={publicSettings} navigate={navigate} enrollingLessonId={enrollingLessonId}
                handleEnroll={startEnroll} getEnrollmentStatus={getEnrollmentStatus}
                t={t} lang={lang} dateFnsLocale={dateFnsLocale} mySubscription={mySubscription}
                myEnrollments={myEnrollments} onSubscribe={handleSubscribe} onCancelSubscription={handleCancelSubscription} onReactivateSubscription={handleReactivateSubscription}
                handleCancelEnrollment={handleCancelEnrollment} canCancel={canCancel}
              />
            } />
            <Route path="/lessons" element={
              <StudentLessonsPage
                lessons={lessons} selectedDate={selectedDate} setSelectedDate={setSelectedDate}
                lessonDates={lessonDates} enrollingLessonId={enrollingLessonId} handleEnroll={startEnroll}
                getEnrollmentStatus={getEnrollmentStatus} myEnrollments={myEnrollments}
                handleCancelEnrollment={handleCancelEnrollment} canCancel={canCancel}
                t={t} lang={lang} dateFnsLocale={dateFnsLocale}
              />
            } />
            <Route path="/progress" element={
              <StudentProgressPage user={user} t={t} />
            } />
          </Routes>

          {/* Activity Selection Dialog */}
          <Dialog open={activityDialog.open} onOpenChange={(open) => !open && setActivityDialog({ open: false, lessonId: null })}>
            <DialogContent className="max-w-xs">
              <DialogHeader>
                <DialogTitle className="text-center">{t("selectActivity")}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <Button 
                  onClick={() => confirmEnroll("surf")}
                  className="bg-[#6697a4] hover:bg-[#3d5a63] h-20 flex flex-col gap-2 rounded-xl"
                  data-testid="activity-surf-btn"
                >
                  <Waves className="h-6 w-6" />
                  <span className="font-semibold">{t("surf")}</span>
                </Button>
                <Button 
                  onClick={() => confirmEnroll("bodyboard")}
                  className="bg-[#e8927c] hover:bg-[#d4735d] h-20 flex flex-col gap-2 rounded-xl"
                  data-testid="activity-bodyboard-btn"
                >
                  <Waves className="h-6 w-6" />
                  <span className="font-semibold">{t("bodyboard")}</span>
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </main>
      </div>
    </div>
  );
};

// Student Home (Overview)
const StudentHome = ({ user, upcomingEnrollments, upcomingAvailable, publicSettings, navigate, enrollingLessonId, handleEnroll, getEnrollmentStatus, t, lang, dateFnsLocale, mySubscription, myEnrollments, onSubscribe, onCancelSubscription, onReactivateSubscription, handleCancelEnrollment, canCancel }) => {
  const currentMonth = new Date().toISOString().slice(0, 7);
  const currentPayment = mySubscription?.payments?.find(p => p.month === currentMonth);
  const isOverdue = currentPayment?.status === "overdue" || (currentPayment?.status === "pending" && new Date().getDate() > 15);

  return (
  <div className="space-y-6">
    {/* 1. Subscription Status */}
    {mySubscription ? (
      <Card className={`border-0 shadow-sm rounded-xl ${mySubscription.status === "cancelling" ? "bg-amber-50 border-l-4 border-l-amber-400" : isOverdue ? "bg-red-50 border-l-4 border-l-red-400" : "bg-purple-50 border-l-4 border-l-purple-400"}`} data-testid="subscription-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${mySubscription.status === "cancelling" ? "bg-amber-100" : isOverdue ? "bg-red-100" : "bg-purple-100"}`}>
                <Award className={`h-5 w-5 ${mySubscription.status === "cancelling" ? "text-amber-600" : isOverdue ? "text-red-600" : "text-purple-600"}`} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-[#3d5a63] text-sm">{lang === "en" ? "Monthly Subscription" : "Subscrição Mensal"}</p>
                  {mySubscription.status === "cancelling" ? (
                    <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px]">{lang === "en" ? "Cancelling" : "A Cancelar"}</Badge>
                  ) : mySubscription.status === "pending_activation" ? (
                    <Badge className="bg-blue-100 text-blue-700 border-0 text-[10px]">{lang === "en" ? "Pending Activation" : "Ativação Pendente"}</Badge>
                  ) : (
                    <Badge className="bg-purple-100 text-purple-700 border-0 text-[10px]">{lang === "en" ? "Active" : "Ativa"}</Badge>
                  )}
                </div>
                <p className="text-xs text-[#94A3B8]">
                  {lang === "en" ? "Since" : "Desde"} {mySubscription.start_date} · {mySubscription.monthly_price}€/{lang === "en" ? "month" : "mês"} · {mySubscription.weekly_credits} {lang === "en" ? "credits/week" : "créd/sem"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={isOverdue ? "bg-red-100 text-red-700 border-0" : currentPayment?.status === "paid" ? "bg-green-100 text-green-700 border-0" : "bg-amber-100 text-amber-700 border-0"}>
                {isOverdue ? (lang === "en" ? "Overdue" : "Em Atraso") : currentPayment?.status === "paid" ? (lang === "en" ? "Paid" : "Pago") : (lang === "en" ? "Pending" : "Pendente")} ({currentMonth})
              </Badge>
              {mySubscription.status !== "cancelling" && (
                <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50 text-xs rounded-full" onClick={onCancelSubscription} data-testid="cancel-subscription-btn">
                  {lang === "en" ? "Cancel" : "Cancelar"}
                </Button>
              )}
              {mySubscription.status === "cancelling" && (
                <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white text-xs rounded-full" onClick={onReactivateSubscription} data-testid="reactivate-subscription-btn">
                  {lang === "en" ? "Reactivate" : "Reativar"}
                </Button>
              )}
            </div>
          </div>
          {mySubscription.status === "cancelling" && (
            <p className="text-xs text-amber-700 mt-2">
              {lang === "en" ? `Your subscription will be cancelled at the end of ${mySubscription.cancel_at || currentMonth}. You can use your credits until then.` : `A tua subscrição será cancelada no final de ${mySubscription.cancel_at || currentMonth}. Podes usar os créditos até lá.`}
            </p>
          )}
          {isOverdue && mySubscription.status !== "cancelling" && (
            <p className="text-xs text-red-600 mt-2">
              {lang === "en" ? "Your monthly payment is overdue. Please pay before the 15th to keep your subscription active." : "O teu pagamento mensal está em atraso. Regulariza até ao dia 15 para manter a subscrição ativa."}
            </p>
          )}
        </CardContent>
      </Card>
    ) : (
      <Card className="border-0 shadow-sm rounded-xl bg-gradient-to-r from-purple-50 to-blue-50 border-l-4 border-l-purple-300" data-testid="subscribe-promo-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                <Sparkles className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="font-semibold text-[#3d5a63] text-sm">{lang === "en" ? "Monthly Plan" : "Programa Mensal"}</p>
                <p className="text-xs text-[#94A3B8]">{lang === "en" ? "Get weekly lessons at a special price" : "Tem aulas semanais a um preço especial"}</p>
              </div>
            </div>
            <Button size="sm" className="bg-purple-600 hover:bg-purple-700 text-white rounded-full px-5 text-xs" onClick={() => {
              if (publicSettings.payments_active) onSubscribe();
              else window.open(`https://api.whatsapp.com/send?phone=${publicSettings.whatsapp_number?.replace(/[^0-9]/g, '') || "351961410360"}&text=Olá! Gostava de subscrever o plano mensal.`, "_blank");
            }} data-testid="subscribe-btn">
              <Award className="h-3.5 w-3.5 mr-1.5" />
              {lang === "en" ? "Subscribe" : "Subscrever"}
            </Button>
          </div>
        </CardContent>
      </Card>
    )}

    {/* 2. Quick Stats + Rankings — unified compact row */}
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="student-stats-row">
      <Card className="border-0 shadow-sm rounded-xl bg-gradient-to-br from-[#6697a4] to-[#3d5a63] text-white">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <CreditCard className="h-4 w-4" />
          </div>
          <div>
            <p className="text-xl font-bold leading-none">{user?.credits || 0}</p>
            <p className="text-[10px] text-white/70 mt-0.5">{t("groupCredits")}</p>
          </div>
        </CardContent>
      </Card>
      <Card className="border-0 shadow-sm rounded-xl bg-gradient-to-br from-[#e8927c] to-[#d4735d] text-white">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <CreditCard className="h-4 w-4" />
          </div>
          <div>
            <p className="text-xl font-bold leading-none">{user?.private_credits || 0}</p>
            <p className="text-[10px] text-white/70 mt-0.5">{t("privateCredits")}</p>
          </div>
        </CardContent>
      </Card>
      <Card className="border-0 shadow-sm rounded-xl">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#6697a4]/10 flex items-center justify-center shrink-0">
            <Waves className="h-4 w-4 text-[#6697a4]" />
          </div>
          <div>
            <p className="text-sm font-bold text-[#3d5a63] leading-none">{user?.ranking_surf || "—"}</p>
            <p className="text-[10px] text-[#94A3B8] mt-0.5">{lang === "en" ? "Surf Level" : "Nível Surf"}</p>
          </div>
        </CardContent>
      </Card>
      <Card className="border-0 shadow-sm rounded-xl">
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#e8927c]/10 flex items-center justify-center shrink-0">
            <Award className="h-4 w-4 text-[#e8927c]" />
          </div>
          <div>
            <p className="text-sm font-bold text-[#3d5a63] leading-none">{user?.ranking_bodyboard || "—"}</p>
            <p className="text-[10px] text-[#94A3B8] mt-0.5">{lang === "en" ? "Bodyboard Level" : "Nível Bodyboard"}</p>
          </div>
        </CardContent>
      </Card>
    </div>

    {/* 3. Active Lesson Check-in Card */}
    <ActiveLessonCheckin 
      enrollments={upcomingEnrollments}
      onCheckinSuccess={() => window.location.reload()}
      lang={lang}
      dateFnsLocale={dateFnsLocale}
      t={t}
    />

    {/* 4. My Upcoming Lessons (excluding active lesson) */}
    <UpcomingLessonsSection
      upcomingEnrollments={upcomingEnrollments}
      handleCancelEnrollment={handleCancelEnrollment}
      canCancel={canCancel}
      lang={lang}
      dateFnsLocale={dateFnsLocale}
      t={t}
    />

    {/* 4. Available Lessons + Buy */}
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-[#3d5a63]">{t("availableLessons")}</h3>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" className="text-[#6697a4] border-[#6697a4]/30 hover:bg-[#6697a4]/5 rounded-full text-xs" onClick={() => {
            if (publicSettings.payments_active) navigate("/packs");
            else window.open(`https://api.whatsapp.com/send?phone=${publicSettings.whatsapp_number?.replace(/[^0-9]/g, '') || "351961410360"}&text=Olá! Gostava de comprar um pack de aulas.`, "_blank");
          }} data-testid="buy-packs-btn">
            <ShoppingBag className="h-3.5 w-3.5 mr-1" />{t("buyLessons")}
          </Button>
          <Link to="/dashboard/lessons" className="text-sm text-[#6697a4] hover:underline">{t("viewAll")}</Link>
        </div>
      </div>
      {upcomingAvailable.length === 0 ? (
        <Card className="border-0 shadow-sm"><CardContent className="p-6 text-center text-[#94A3B8]">{t("noAvailableLessons")}</CardContent></Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {upcomingAvailable.map(lesson => (
            <Card key={lesson.lesson_id} className="bg-white border-0 shadow-sm rounded-xl hover:shadow-md transition-shadow" data-testid={`available-lesson-${lesson.lesson_id}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className="bg-[#6697a4]/10 text-[#6697a4] border-0 text-xs">
                    {lesson.lesson_category === "surf" ? t("surf") : lesson.lesson_category === "bodyboard" ? t("bodyboard") : `${t("surf")} & ${t("bodyboard")}`}
                  </Badge>
                </div>
                <div className="space-y-1 text-xs text-[#475569] mb-3 mt-2">
                  <div className="flex items-center gap-2"><CalendarIcon className="h-3.5 w-3.5 text-[#6697a4]" />{format(parseISO(lesson.date), "EEE, d MMM", { locale: dateFnsLocale })}</div>
                  <div className="flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-[#6697a4]" />{lesson.start_time} - {lesson.end_time}</div>
                  <div className="flex items-center gap-2"><Users className="h-3.5 w-3.5 text-[#6697a4]" />{lesson.current_enrollments}/{lesson.max_students}</div>
                  {lesson.teachers_data?.length > 0 && (
                    <div className="flex items-center gap-1.5">
                      <div className="flex -space-x-1.5">{lesson.teachers_data.map(t_ => (
                        <div key={t_.user_id} className="w-4 h-4 rounded-full bg-[#6697a4] text-white text-[7px] flex items-center justify-center border border-white overflow-hidden" title={t_.name}>
                          {t_.picture ? <img src={t_.picture} alt="" className="w-full h-full object-cover" /> : t_.name?.charAt(0)}
                        </div>
                      ))}</div>
                      <span className="text-[10px]">{lesson.teachers_data.map(t_ => t_.name).join(", ")}</span>
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-between pt-2 border-t">
                  <span className="text-base font-bold text-[#6697a4]">{lesson.price}€</span>
                  <Button size="sm" className="bg-[#6697a4] hover:bg-[#3d5a63] text-white rounded-full px-4 text-xs"
                    disabled={enrollingLessonId === lesson.lesson_id}
                    onClick={() => handleEnroll(lesson.lesson_id)}
                    data-testid={`enroll-btn-${lesson.lesson_id}`}
                  >{enrollingLessonId === lesson.lesson_id ? "..." : t("enroll")}</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  </div>
  );
};

// Student Lessons Page (Calendar + lesson list)
const StudentLessonsPage = ({ lessons, selectedDate, setSelectedDate, lessonDates, enrollingLessonId, handleEnroll, getEnrollmentStatus, myEnrollments, handleCancelEnrollment, canCancel, t, lang, dateFnsLocale }) => {
  const selectedDayLessons = lessons.filter(l => l.date === format(selectedDate, "yyyy-MM-dd"));
  const upcoming = myEnrollments.filter(e => ["enrolled", "confirmed", "reserved"].includes(e.status) && e.lesson?.date && isAfter(parseISO(e.lesson.date), new Date()));
  const past = myEnrollments.filter(e => !upcoming.includes(e));

  const EnrollmentCard = ({ enrollment }) => {
    const catLabel = enrollment.lesson?.lesson_category === "surf" ? "Surf" : enrollment.lesson?.lesson_category === "bodyboard" ? "Bodyboard" : "Surf & Bodyboard";
    const isPast = enrollment.lesson?.date && !isAfter(parseISO(enrollment.lesson.date), new Date());
    return (
      <Card className={`border-0 shadow-sm rounded-xl transition-shadow hover:shadow-md ${isPast ? "opacity-70" : ""}`} data-testid={`enrollment-${enrollment.enrollment_id}`}>
        <CardContent className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${isPast ? "bg-slate-100" : "bg-[#6697a4]/10"}`}>
                <Waves className={`h-5 w-5 ${isPast ? "text-slate-400" : "text-[#6697a4]"}`} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-semibold text-[#3d5a63] text-sm">{catLabel}</p>
                  {enrollment.activity_type && (
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">{enrollment.activity_type === "surf" ? "Surf" : "Bodyboard"}</Badge>
                  )}
                </div>
                <p className="text-xs text-[#94A3B8] mt-1">
                  {enrollment.lesson?.date && format(parseISO(enrollment.lesson.date), "EEEE, d MMM", { locale: dateFnsLocale })} · {enrollment.lesson?.start_time} - {enrollment.lesson?.end_time}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Badge className={
                enrollment.status === "attended" ? "bg-green-100 text-green-700 border-0 text-xs" :
                enrollment.status === "reserved" ? "bg-amber-100 text-amber-700 border-0 text-xs" :
                enrollment.status === "cancelled" ? "bg-red-100 text-red-700 border-0 text-xs" :
                "bg-blue-100 text-blue-700 border-0 text-xs"
              }>
                {enrollment.status === "attended" ? t("attended") : enrollment.status === "reserved" ? t("reserved") : enrollment.status === "cancelled" ? t("cancelled") : t("enrolled")}
              </Badge>
              {["enrolled", "confirmed", "reserved"].includes(enrollment.status) && !isPast && canCancel(enrollment) && (
                <Button variant="ghost" size="sm" onClick={() => handleCancelEnrollment(enrollment.enrollment_id)} className="text-red-500 hover:text-red-700 hover:bg-red-50 text-xs h-7 px-2" data-testid={`cancel-enrollment-${enrollment.enrollment_id}`}>
                  {t("cancelEnrollment")}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-8">
      <h2 className="text-xl font-bold text-[#3d5a63]">{t("lessons")}</h2>
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="bg-white border-0 shadow-sm rounded-xl md:col-span-1 h-fit">
          <CardContent className="p-3 flex justify-center">
            <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)} locale={dateFnsLocale} className="rounded-lg"
              modifiers={{ hasLesson: lessonDates }} modifiersStyles={{ hasLesson: { backgroundColor: "#6697a4", color: "white", borderRadius: "50%" } }}
            />
          </CardContent>
        </Card>
        <div className="md:col-span-2 space-y-4">
          <h3 className="text-base font-semibold text-[#3d5a63] mb-2">{format(selectedDate, "EEEE, d MMMM", { locale: dateFnsLocale })}</h3>
          {selectedDayLessons.length === 0 ? (
            <Card className="border-0 shadow-sm"><CardContent className="p-6 text-center text-[#94A3B8] text-sm">{t("noLessonsToday")}</CardContent></Card>
          ) : (
            selectedDayLessons.map(lesson => {
              const enrollment = getEnrollmentStatus(lesson.lesson_id);
              const isFull = lesson.current_enrollments >= lesson.max_students;
              return (
                <Card key={lesson.lesson_id} className="bg-white border-0 shadow-sm rounded-xl" data-testid={`lesson-card-${lesson.lesson_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className="bg-[#6697a4]/10 text-[#6697a4] border-0 text-xs">
                        {lesson.lesson_category === "surf" ? t("surf") : lesson.lesson_category === "bodyboard" ? t("bodyboard") : `${t("surf")} & ${t("bodyboard")}`}
                      </Badge>
                      <Badge variant="outline" className="text-xs">{lesson.lesson_type === "private" ? t("privateLesson") : t("group")}</Badge>
                    </div>
                    <h4 className="font-semibold text-[#3d5a63] mb-2">{lesson.lesson_category === "surf" ? "Surf" : lesson.lesson_category === "bodyboard" ? "Bodyboard" : "Surf & Bodyboard"}</h4>
                    <div className="flex items-center gap-4 text-xs text-[#475569] mb-3">
                      <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{lesson.start_time} - {lesson.end_time}</span>
                      <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{lesson.current_enrollments}/{lesson.max_students}</span>
                    </div>
                    {lesson.teachers_data?.length > 0 && (
                      <div className="flex items-center gap-1.5 mb-3 text-xs text-[#475569]">
                        <div className="flex -space-x-1.5">{lesson.teachers_data.map(t_ => (
                          <div key={t_.user_id} className="w-5 h-5 rounded-full bg-[#6697a4] text-white text-[8px] flex items-center justify-center border border-white overflow-hidden">
                            {t_.picture ? <img src={t_.picture} alt="" className="w-full h-full object-cover" /> : t_.name?.charAt(0)}
                          </div>
                        ))}</div>
                        <span>{lesson.teachers_data.map(t_ => t_.name).join(", ")}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-2 border-t">
                      <span className="text-lg font-bold text-[#6697a4]">{lesson.price}€</span>
                      {enrollment ? (
                        <div className="flex items-center gap-2">
                          <Badge className={enrollment.status === "attended" ? "bg-green-100 text-green-700 border-0" : enrollment.status === "reserved" ? "bg-amber-100 text-amber-700 border-0" : "bg-blue-100 text-blue-700 border-0"}>
                            {enrollment.status === "attended" ? t("attended") : enrollment.status === "reserved" ? t("reserved") : t("enrolled")}
                          </Badge>
                          {canCancel(enrollment) && isAfter(parseISO(lesson.date), new Date()) && (
                            <Button variant="outline" size="sm" onClick={() => handleCancelEnrollment(enrollment.enrollment_id)} className="text-red-600 border-red-200 hover:bg-red-50 text-xs">{t("cancelEnrollment")}</Button>
                          )}
                        </div>
                      ) : (
                        <Button size="sm" className="bg-[#6697a4] hover:bg-[#3d5a63] text-white rounded-full px-4 text-xs"
                          disabled={isFull || enrollingLessonId === lesson.lesson_id}
                          onClick={() => handleEnroll(lesson.lesson_id)}
                        >{isFull ? t("full") : enrollingLessonId === lesson.lesson_id ? "..." : t("enroll")}</Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      </div>

      {/* My Enrollments Section */}
      {myEnrollments.length > 0 && (
        <div className="space-y-6 pt-4 border-t border-slate-100">
          <h2 className="text-xl font-bold text-[#3d5a63]">{t("myEnrollments")}</h2>

          {/* Upcoming */}
          {upcoming.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-[#6697a4]" />
                <h3 className="text-sm font-semibold text-[#3d5a63] uppercase tracking-wider">{lang === "en" ? "Upcoming" : "Próximas"} ({upcoming.length})</h3>
              </div>
              <div className="space-y-5">
                {upcoming.map(e => <EnrollmentCard key={e.enrollment_id} enrollment={e} />)}
              </div>
            </div>
          )}

          {/* History */}
          {past.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-slate-300" />
                <h3 className="text-sm font-semibold text-[#94A3B8] uppercase tracking-wider">{lang === "en" ? "History" : "Histórico"} ({past.length})</h3>
              </div>
              <div className="space-y-5">
                {past.map(e => <EnrollmentCard key={e.enrollment_id} enrollment={e} />)}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Student Progress Page (reviews + rankings)
const StudentProgressPage = ({ user, t }) => {
  const { lang } = useTranslation();
  
  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-[#3d5a63]">{t("myProgress")}</h2>

      {/* Rankings */}
      {(user?.ranking_surf || user?.ranking_bodyboard) && (
        <div className="grid sm:grid-cols-2 gap-4">
          {user?.ranking_surf && (
            <Card className="border-0 shadow-sm"><CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#6697a4]/10 flex items-center justify-center"><Star className="h-5 w-5 text-[#F59E0B]" /></div>
              <div><p className="text-xs text-[#94A3B8]">{t("rankingSurf")}</p><p className="font-bold text-[#3d5a63]">{user.ranking_surf}</p></div>
            </CardContent></Card>
          )}
          {user?.ranking_bodyboard && (
            <Card className="border-0 shadow-sm"><CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#e8927c]/10 flex items-center justify-center"><Star className="h-5 w-5 text-[#e8927c]" /></div>
              <div><p className="text-xs text-[#94A3B8]">{t("rankingBodyboard")}</p><p className="font-bold text-[#3d5a63]">{user.ranking_bodyboard}</p></div>
            </CardContent></Card>
          )}
        </div>
      )}

      {/* Unified Feedback / Logbook */}
      <div>
        <h3 className="text-base font-semibold text-[#3d5a63] mb-4">{lang === "en" ? "Feedbacks & Progress Notes" : "Feedbacks e Notas de Progresso"}</h3>
        <Logbook />
      </div>
    </div>
  );
};

// Active Lesson Check-in Card Component
const ActiveLessonCheckin = ({ enrollments, onCheckinSuccess, lang, dateFnsLocale, t }) => {
  const [checkinCode, setCheckinCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [showCodeInput, setShowCodeInput] = useState(false);

  const now = new Date();
  
  // Get the next upcoming enrollment (card visible until 2h after lesson ends)
  const getNextEnrollment = () => {
    // Sort by date/time to get the nearest lesson first
    const sorted = [...enrollments]
      .filter(e => e.lesson && !e.attended)
      .sort((a, b) => {
        const dateA = new Date(`${a.lesson.date}T${a.lesson.start_time || "09:00"}:00`);
        const dateB = new Date(`${b.lesson.date}T${b.lesson.start_time || "09:00"}:00`);
        return dateA - dateB;
      });

    for (const enrollment of sorted) {
      const lessonDate = enrollment.lesson.date;
      const startTime = enrollment.lesson.start_time || "09:00";
      const endTime = enrollment.lesson.end_time || "11:00";
      
      try {
        const lessonStart = new Date(`${lessonDate}T${startTime}:00`);
        const lessonEnd = new Date(`${lessonDate}T${endTime}:00`);
        const windowEnd = addHours(lessonEnd, 2);
        const checkinWindowStart = subHours(lessonStart, 1);
        
        // Card visible until 2h after lesson ends
        if (now <= windowEnd) {
          return {
            enrollment,
            lessonStart,
            lessonEnd,
            isBeforeCheckinWindow: now < checkinWindowStart,
            isInCheckinWindow: now >= checkinWindowStart && now <= windowEnd,
            isAfterEnd: now > lessonEnd
          };
        }
      } catch {
        continue;
      }
    }
    return null;
  };

  // Find recently attended lesson (to show confirmation)
  const getAttendedRecently = () => {
    for (const enrollment of enrollments) {
      if (!enrollment.lesson) continue;
      if (!enrollment.attended) continue;
      
      const lessonDate = enrollment.lesson.date;
      const endTime = enrollment.lesson.end_time || "11:00";
      
      try {
        const lessonEnd = new Date(`${lessonDate}T${endTime}:00`);
        const windowEnd = addHours(lessonEnd, 2);
        
        if (now <= windowEnd) {
          return enrollment;
        }
      } catch {
        continue;
      }
    }
    return null;
  };

  const nextEnrollment = getNextEnrollment();
  const attendedRecently = getAttendedRecently();

  const handleCheckin = async () => {
    if (!checkinCode.trim()) {
      toast.error(lang === "en" ? "Enter the attendance code" : "Insere o código de presença");
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/checkin/${checkinCode.trim().toUpperCase()}`, {}, { withCredentials: true });
      toast.success(res.data.message);
      if (onCheckinSuccess) onCheckinSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "en" ? "Check-in error" : "Erro no check-in"));
    } finally {
      setLoading(false);
    }
  };

  // Show attendance confirmed card (takes priority)
  if (attendedRecently) {
    const lesson = attendedRecently.lesson;
    return (
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-0 shadow-sm rounded-xl border-l-4 border-l-green-500" data-testid="attendance-confirmed-card">
        <CardContent className="p-5">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-green-100 flex items-center justify-center shrink-0">
              <CheckCircle className="h-7 w-7 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-green-600 font-medium mb-1">
                {lang === "en" ? "Attendance confirmed" : "Presença confirmada"}
              </p>
              <p className="font-semibold text-[#3d5a63]">
                {lesson?.lesson_category === "surf" ? "Surf" : lesson?.lesson_category === "bodyboard" ? "Bodyboard" : "Surf & Bodyboard"}
              </p>
              <p className="text-sm text-[#94A3B8]">
                {lesson?.date && format(parseISO(lesson.date), "EEEE, d MMMM", { locale: dateFnsLocale })} · {lesson?.start_time} - {lesson?.end_time}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // No upcoming lesson
  if (!nextEnrollment) return null;

  const { enrollment, isBeforeCheckinWindow, isInCheckinWindow, isAfterEnd } = nextEnrollment;
  const lesson = enrollment.lesson;

  // Determine card title based on timing
  const getTitle = () => {
    if (isAfterEnd) return lang === "en" ? "Your last lesson" : "A tua última aula";
    return lang === "en" ? "Your next lesson" : "A tua próxima aula";
  };

  return (
    <Card className="bg-gradient-to-r from-[#6697a4]/5 to-[#3d5a63]/5 border-0 shadow-sm rounded-xl border-l-4 border-l-[#6697a4]" data-testid="active-lesson-checkin-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-[#6697a4]/10 flex items-center justify-center shrink-0">
              <Waves className="h-7 w-7 text-[#6697a4]" />
            </div>
            <div>
              <p className="text-xs text-[#6697a4] font-medium mb-1">{getTitle()}</p>
              <p className="font-semibold text-[#3d5a63]">
                {lesson?.lesson_category === "surf" ? "Surf" : lesson?.lesson_category === "bodyboard" ? "Bodyboard" : "Surf & Bodyboard"}
              </p>
              <p className="text-sm text-[#94A3B8]">
                {lesson?.date && format(parseISO(lesson.date), "EEEE, d MMMM", { locale: dateFnsLocale })} · {lesson?.start_time} - {lesson?.end_time}
              </p>
              {lesson?.teachers_data?.length > 0 && (
                <p className="text-xs text-[#94A3B8] mt-1">
                  {lang === "en" ? "Instructor" : "Instrutor"}: {lesson.teachers_data.map(t_ => t_.name).join(", ")}
                </p>
              )}
            </div>
          </div>

          {/* Check-in Section - Only visible in check-in window */}
          {isInCheckinWindow && (
            <div className="flex flex-col items-end gap-2">
              {!showCodeInput ? (
                <Button
                  onClick={() => setShowCodeInput(true)}
                  className="bg-[#6697a4] hover:bg-[#3d5a63] text-white rounded-full px-5"
                  data-testid="show-checkin-input-btn"
                >
                  <QrCode className="h-4 w-4 mr-2" />
                  {lang === "en" ? "Check-in" : "Registar Presença"}
                </Button>
              ) : (
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={checkinCode}
                      onChange={(e) => setCheckinCode(e.target.value.toUpperCase())}
                      placeholder={lang === "en" ? "Enter code" : "Código"}
                      className="w-28 px-3 py-2 text-sm font-mono tracking-wider border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6697a4] text-center uppercase"
                      maxLength={12}
                      autoFocus
                      data-testid="inline-checkin-code-input"
                    />
                    <Button
                      onClick={handleCheckin}
                      disabled={loading || !checkinCode.trim()}
                      className="bg-[#6697a4] hover:bg-[#3d5a63] text-white rounded-lg"
                      data-testid="inline-checkin-submit-btn"
                    >
                      {loading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Check className="h-4 w-4" />}
                    </Button>
                  </div>
                  <button
                    onClick={() => { setShowCodeInput(false); setCheckinCode(""); }}
                    className="text-xs text-[#94A3B8] hover:text-[#6697a4]"
                  >
                    {lang === "en" ? "Cancel" : "Cancelar"}
                  </button>
                </div>
              )}
              <p className="text-[10px] text-[#94A3B8] text-right max-w-[200px]">
                {lang === "en" 
                  ? "Ask your instructor for the attendance code" 
                  : "Pede o código de presença ao instrutor"}
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Upcoming Lessons Section (excludes the first upcoming lesson shown in ActiveLessonCheckin)
const UpcomingLessonsSection = ({ upcomingEnrollments, handleCancelEnrollment, canCancel, lang, dateFnsLocale, t }) => {
  const now = new Date();
  
  // Sort enrollments by date to identify which one is shown in the card
  const sortedEnrollments = [...upcomingEnrollments]
    .filter(e => e.lesson && !e.attended)
    .sort((a, b) => {
      const dateA = new Date(`${a.lesson.date}T${a.lesson.start_time || "09:00"}:00`);
      const dateB = new Date(`${b.lesson.date}T${b.lesson.start_time || "09:00"}:00`);
      return dateA - dateB;
    });

  // Find the first enrollment that's still visible in the card (not expired 2h after)
  let firstVisibleEnrollmentId = null;
  for (const enrollment of sortedEnrollments) {
    try {
      const lessonEnd = new Date(`${enrollment.lesson.date}T${enrollment.lesson.end_time || "11:00"}:00`);
      const windowEnd = addHours(lessonEnd, 2);
      if (now <= windowEnd) {
        firstVisibleEnrollmentId = enrollment.enrollment_id;
        break;
      }
    } catch {
      continue;
    }
  }

  // Filter: exclude attended, exclude the one shown in card, exclude expired
  const filteredEnrollments = sortedEnrollments.filter(enrollment => {
    // Already excluded attended above
    // Exclude the first visible one (shown in the card)
    if (enrollment.enrollment_id === firstVisibleEnrollmentId) return false;
    
    try {
      const lessonEnd = new Date(`${enrollment.lesson.date}T${enrollment.lesson.end_time || "11:00"}:00`);
      const windowEnd = addHours(lessonEnd, 2);
      // Only show if not yet expired
      return now <= windowEnd;
    } catch {
      return true;
    }
  });

  if (filteredEnrollments.length === 0) return null;

  return (
    <div>
      <h3 className="text-lg font-semibold text-[#3d5a63] mb-4">{t("myUpcomingLessons")}</h3>
      <div className="space-y-3">
        {filteredEnrollments.map(enrollment => (
          <Card key={enrollment.enrollment_id} className="bg-white border-0 shadow-sm rounded-xl" data-testid={`upcoming-${enrollment.enrollment_id}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#6697a4]/10 flex items-center justify-center">
                  <Waves className="h-5 w-5 text-[#6697a4]" />
                </div>
                <div>
                  <p className="font-medium text-[#3d5a63] text-sm">{enrollment.lesson?.lesson_category === "surf" ? "Surf" : enrollment.lesson?.lesson_category === "bodyboard" ? "Bodyboard" : "Surf & Bodyboard"}</p>
                  <p className="text-xs text-[#94A3B8]">{enrollment.lesson?.date && format(parseISO(enrollment.lesson.date), "EEE, d MMM", { locale: dateFnsLocale })} · {enrollment.lesson?.start_time}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={enrollment.status === "reserved" ? "bg-amber-100 text-amber-700 border-0" : "bg-green-100 text-green-700 border-0"}>
                  {enrollment.status === "reserved" ? t("reserved") : t("enrolled")}
                </Badge>
                {canCancel(enrollment) && (
                  <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50 h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); handleCancelEnrollment(enrollment.enrollment_id); }} data-testid={`home-cancel-enrollment-${enrollment.enrollment_id}`}>
                    <X className="h-3 w-3 mr-1" />{t("cancelEnrollment")}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default StudentDashboard;
